import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { QrCode, RefreshCw } from 'lucide-react';
import { usersService, type UserQrCode } from '../../services/users';

const QR_SIZE = 224;
/** Kept under a quarter of the code so level H redundancy can recover it. */
const LOGO_SIZE = 48;

interface ProfileQrCodeProps {
  /** Slug of the signed-in climber whose profile is being viewed. */
  userSlug: string;
}

/**
 * Button under the avatar that opens the climber's identity QR code.
 *
 * The payload is minted server-side on every profile visit, so the code shown
 * is always fresh; it carries a short-lived signature and expires on its own.
 */
export default function ProfileQrCode({ userSlug }: ProfileQrCodeProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [qrCode, setQrCode] = useState<UserQrCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setQrCode(await usersService.getQrCode(userSlug));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [userSlug]);

  // A code minted on page load would already be stale by the time the dialog
  // opens, so it is generated on visit of this view instead.
  useEffect(() => {
    generate();
  }, [generate]);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <QrCode className="h-4 w-4 mr-2" />
        {t('userProfile.qrCode.button')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('userProfile.qrCode.title')}</DialogTitle>
            <DialogDescription>{t('userProfile.qrCode.description')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-4 py-2">
            {loading && <Skeleton className="h-56 w-56" />}

            {!loading && error && (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('userProfile.qrCode.error')}
                </p>
                <Button variant="outline" size="sm" onClick={generate}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('userProfile.qrCode.retry')}
                </Button>
              </div>
            )}

            {!loading && !error && qrCode && (
              <>
                {/* White backing keeps the code scannable in dark mode. */}
                <div className="relative rounded-lg bg-white p-4">
                  {/*
                    Level H tolerates ~30% of the code being obscured, which is
                    what lets the logo sit on top of the middle of it.
                  */}
                  <QRCode value={qrCode.qr_url} size={QR_SIZE} level="H" />
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <div className="rounded-md bg-white p-1 shadow-sm">
                      <img
                        src="/icons/icon-192x192.svg"
                        alt=""
                        style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                      />
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-semibold">{qrCode?.full_name}</p>
                  <p className="text-sm text-muted-foreground">@{qrCode?.username}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={generate}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('userProfile.qrCode.regenerate')}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
